import { supabase } from '../config/supabase';
import agent from '../agent';

// ─── Room helpers ─────────────────────────────────────────────────────────────

export const getOrCreateRoom = async (entityType, entityId, entityName, currentUsername, participantUsernames = []) => {
  const { data: existing } = await supabase
    .from('chat_rooms')
    .select('*')
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .eq('type', 'group')
    .limit(1);

  let room;

  if (existing && existing.length > 0) {
    room = existing[0];
  } else {
    const { data: created, error } = await supabase
      .from('chat_rooms')
      .insert({ type: 'group', name: entityName, entity_type: entityType, entity_id: entityId })
      .select()
      .single();
    if (error) throw new Error(error.message);
    room = created;

    const allUsernames = Array.from(new Set([currentUsername, ...participantUsernames]));
    const { error: membersError } = await supabase.from('chat_room_members').insert(
      allUsernames.map(u => ({ room_id: room.id, username: u })),
    );
    if (membersError) console.warn('Failed to seed room members:', membersError.message);
    return room;
  }

  await supabase
    .from('chat_room_members')
    .upsert(
      { room_id: room.id, username: currentUsername },
      { onConflict: 'room_id,username', ignoreDuplicates: true },
    );

  return room;
};

export const getOrCreateDmRoom = async (currentUsername, otherUsername) => {
  const { data: myMemberships } = await supabase
    .from('chat_room_members')
    .select('room_id')
    .eq('username', currentUsername);

  const myRoomIds = (myMemberships || []).map(m => m.room_id);

  if (myRoomIds.length > 0) {
    const { data: sharedMemberships } = await supabase
      .from('chat_room_members')
      .select('room_id')
      .eq('username', otherUsername)
      .in('room_id', myRoomIds);

    const sharedIds = (sharedMemberships || []).map(m => m.room_id);

    if (sharedIds.length > 0) {
      const { data: dmRooms } = await supabase
        .from('chat_rooms')
        .select('*')
        .in('id', sharedIds)
        .eq('type', 'dm')
        .limit(1);

      if (dmRooms && dmRooms.length > 0) return dmRooms[0];
    }
  }

  const { data: room, error } = await supabase
    .from('chat_rooms')
    .insert({ type: 'dm', name: otherUsername })
    .select()
    .single();
  if (error) throw new Error(error.message);

  await supabase.from('chat_room_members').insert([
    { room_id: room.id, username: currentUsername },
    { room_id: room.id, username: otherUsername },
  ]);

  return room;
};

// ─── Inbox ────────────────────────────────────────────────────────────────────

export const getRooms = async (currentUsername) => {
  const { data: memberships } = await supabase
    .from('chat_room_members')
    .select('*, chat_rooms(*)')
    .eq('username', currentUsername);

  if (!memberships || memberships.length === 0) return [];

  const result = [];

  for (const membership of memberships) {
    const room = membership.chat_rooms;

    const { data: msgs } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('room_id', room.id)
      .order('created_at', { ascending: false })
      .limit(1);

    const lastMessage = msgs?.[0];

    const { count } = await supabase
      .from('chat_messages')
      .select('*', { count: 'exact', head: true })
      .eq('room_id', room.id)
      .gt('created_at', membership.last_read_at);

    result.push({ ...room, lastMessage, unreadCount: count || 0 });
  }

  return result.sort((a, b) => {
    const aTime = a.lastMessage?.created_at || a.created_at;
    const bTime = b.lastMessage?.created_at || b.created_at;
    return bTime.localeCompare(aTime);
  });
};

// ─── Members ──────────────────────────────────────────────────────────────────

export const getRoomMembers = async (roomId) => {
  const { data } = await supabase
    .from('chat_room_members')
    .select('*')
    .eq('room_id', roomId);
  return data || [];
};

// ─── Messages ─────────────────────────────────────────────────────────────────

export const getMessages = async (roomId, cursor) => {
  let query = supabase
    .from('chat_messages')
    .select('*, reactions:chat_reactions(*), reply_to:chat_messages!reply_to_id(id, content, sender_username)')
    .eq('room_id', roomId)
    .order('created_at', { ascending: false })
    .limit(50);

  if (cursor) {
    query = query.lt('created_at', cursor);
  }

  const { data } = await query;
  return (data || []).reverse();
};

export const sendMessage = async (roomId, content, senderUsername, replyToId = null) => {
  const payload = { room_id: roomId, sender_username: senderUsername, content };
  if (replyToId) payload.reply_to_id = replyToId;
  const { data, error } = await supabase
    .from('chat_messages')
    .insert(payload)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const deleteMessage = async (messageId) => {
  const { error } = await supabase
    .from('chat_messages')
    .delete()
    .eq('id', messageId);
  if (error) throw new Error(error.message);
};

export const editMessage = async (messageId, content) => {
  const { error } = await supabase
    .from('chat_messages')
    .update({ content, edited_at: new Date().toISOString() })
    .eq('id', messageId);
  if (error) throw new Error(error.message);
};

export const markRead = async (roomId, username) => {
  try {
    await supabase
      .from('chat_room_members')
      .update({ last_read_at: new Date().toISOString() })
      .eq('room_id', roomId)
      .eq('username', username);
  } catch {
    // best-effort
  }
};

// ─── Reactions ────────────────────────────────────────────────────────────────

export const addReaction = async (messageId, emoji, username) => {
  await supabase
    .from('chat_reactions')
    .upsert(
      { message_id: messageId, username, emoji },
      { onConflict: 'message_id,username,emoji', ignoreDuplicates: true },
    );
};

export const removeReaction = async (messageId, emoji, username) => {
  await supabase
    .from('chat_reactions')
    .delete()
    .eq('message_id', messageId)
    .eq('username', username)
    .eq('emoji', emoji);
};

// ─── Realtime ─────────────────────────────────────────────────────────────────

export const subscribeToMessages = (roomId, callback) => {
  return supabase
    .channel(`chat-messages-${roomId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `room_id=eq.${roomId}` },
      (payload) => callback(payload.new),
    )
    .subscribe();
};

export const subscribeToInbox = (callback) => {
  return supabase
    .channel('chat-inbox-signal')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'chat_messages' },
      callback,
    )
    .subscribe();
};

// ─── Leave room ───────────────────────────────────────────────────────────────

export const leaveRoom = async (roomId, username) => {
  const { error } = await supabase
    .from('chat_room_members')
    .delete()
    .eq('room_id', roomId)
    .eq('username', username);
  if (error) throw new Error(error.message);
};

// ─── Push notify ──────────────────────────────────────────────────────────────

export const notifyChatMembers = async (members, senderUsername, content) => {
  const recipientUsernames = members
    .map(m => m.username)
    .filter(u => u !== senderUsername);

  if (recipientUsernames.length === 0) return;

  try {
    await agent.Chat.notify(recipientUsernames, content);
  } catch {
    // fire-and-forget
  }
};
